import React, {useEffect, useState} from "react";
import {Badge, Button, Icon, Table, TableData} from "peggirkit";
import {DayReportRefund, DayReportRefundType} from "@/data/entities/dayReport";
import {FormattedMessage, useIntl} from "react-intl";
import {posOrders} from "@/routing/Router";
import {ArrowTopRightOnSquareIcon} from "@heroicons/react/24/outline";
import BadgeAction from "peggirkit/dist/components/Badge/BadgeElements/BadgeAction";
import {InformationCircleIcon} from "@heroicons/react/16/solid";
import CodeFormatter from "@widgets/employees/overview/table/columns/CodeFormatter";
import {formatMoney} from "@/utils/MoneyUtils";
import CancellationInfoModal from "@widgets/reports/dayReport/refunds/CancellationInfoModal";

type Props = {
    refunds: DayReportRefund[],
};

const RefundsTable = ({refunds}: Props) => {
    const intl = useIntl();
    const [data, setData] = useState<TableData<DayReportRefund>[]>(refunds);
    const [showCancellationInfoModal, setShowCancellationInfoModal] = useState<DayReportRefund | null>(null);

    // Keep the table in sync when the report is refetched or another day is picked
    useEffect(() => setData(refunds), [refunds]);

    return (
        <>
            <Table<DayReportRefund>
                columns={[
                    {
                        id: "orderNumber",
                        label: {
                            displayName: intl.formatMessage({id: "dayReport.refund.orderColumn"}),
                        },
                        primary: true,
                        formatter: (orderNumber, {cartPublicId}) => (
                            <Button
                                variant={"link"}
                                iconAfter={<Icon icon={ArrowTopRightOnSquareIcon} className={"!size-4"}/>}
                                buttonAction={{
                                    as: "reactRouterLink",
                                    actionProps: {
                                        to: posOrders(cartPublicId).absolutePath(),
                                        target: "_blank",
                                    },
                                }}
                            >
                                <FormattedMessage
                                    id={"dayReport.refund.order"}
                                    values={{number: orderNumber}}
                                />
                            </Button>
                        ),
                    },
                    {
                        id: "mollieTransactionId",
                        label: {displayName: intl.formatMessage({id: "dayReport.refund.paymentColumn"})},
                        align: "right",
                        primary: false,
                        formatter: (mollieTransactionId: string) => <CodeFormatter code={mollieTransactionId}/>,
                    },
                    {
                        id: "amount",
                        label: {displayName: intl.formatMessage({id: "dayReport.refund.amountColumn"})},
                        align: "right",
                        primary: false,
                        formatter: (amount: number,) => formatMoney(amount),
                    },
                    {
                        id: "type",
                        label: {
                            displayName: intl.formatMessage({id: "dayReport.refund.reasonColumn"}),
                        },
                        primary: false,
                        align: "right",
                        formatter: (type: DayReportRefundType, refund) => (
                            <Badge
                                containerClassname={"inline-flex"}
                                color={type === "CANCELLED" ? "danger" : "warning"}
                                size={"xs"}
                                action={
                                    refund.cancellationReason &&
                                    <BadgeAction
                                        icon={<Icon icon={InformationCircleIcon}/>}
                                        title={intl.formatMessage({id: "dayReport.refund.reasonColumn"})}
                                        disabled={false}
                                        buttonAction={{
                                            as: "button",
                                            actionProps: {
                                                onClick: () => setShowCancellationInfoModal(refund),
                                            },
                                        }}
                                    />
                                }
                            >
                                <FormattedMessage
                                    id={type === "CANCELLED" ? "dayReport.refund.cancelled" : "dayReport.refund.totalLowered"}
                                />
                            </Badge>
                        ),
                    },
                ]}
                data={data}
                setData={setData}
                rowConfig={{
                    hoverEffect: true,
                }}
            />

            <CancellationInfoModal
                refund={showCancellationInfoModal}
                onClose={() => setShowCancellationInfoModal(null)}
            />
        </>
    );
};

export default RefundsTable;